/* eslint-disable max-lines */
import Immutable from 'immutable';
import isAlphanumeric from 'validator/lib/isAlphanumeric';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import { createStructuredSelector } from 'reselect';
import qs from 'qs';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import getMessagingInstance from 'services/MessagingService/MessagingService';
import {
  GET_USER_CONSENT_STATUS,
  GET_USER_CONSENT_STATUS_ERROR,
  GET_USER_CONSENT_STATUS_RESPONSE,
  GET_USER_DATA_RESPONSE,
  SET_USER_GENDER_PREFERENCE,
  USER_LOGGED_OUT,
} from 'services/MessagingService/EventNames';
import TracingService from 'services/TracingService';
import trackingService from 'services/UITrackingService';
import {
  isAnyTypeOfGold as isAnyTypeOfGoldHelper,
  GUEST,
  GOLD,
} from 'pages/User/AccessLevel';
import {
  PASS_REC_EMAIL_SENT,
  WEAK_PASSWORD,
} from 'enum/helpers';
import { TRACKING } from 'enum/localStorageQueryParams';
import { IMPERSONATE_KEY } from 'enum/profile';
import {
  usernameOrImpersonate,
  validateStrongerPassword,
} from 'utils/helpers';
import parseCatch from 'utils/parseCatch';

import * as USER_STATE from './UserState';
import {
  default as api,
  CHANGE_PASSSWORD_URL,
  GENDER_PREFERENCE_URL,
  LOGIN_SECURITY_QUESTIONS_URL,
  LOGIN_URL,
  LOGIN_VERIFY_SECURITY_QUESTIONS_URL,
  LOGOUT_URL,
  PASSWORD_RECOVERY_URL,
  REGISTRATION_URL,
  STUDIO_EMAIL_SETTINGS,
  STUDIO_REGISTRATION_URL,
  USER_CONSENT_URL_STATUS_APPROVED,
  USER_CREATE_URL,
  USER_STATE_URL,
  USER_STATUS_URL,
  VALIDATE_EMAIL_REGISTRATION_URL,
  VALIDATE_USERNAME_REGISTRATION_URL,
} from './Api';
import {
  CODES,
  EMAIL_CONFIRMED,
  ENTER_EMAIL,
  INVALID_EMAIL,
  INVALID_SECONDARY_EMAIL,
  UNVERIFIED_EMAIL,
} from './PasswordRecovery/Messages';


const MessagingService = getMessagingInstance();
const AUTHENTICATE_USER = 'cam4/user/AUTHENTICATE_USER';
const CHANGE_GENDER_PREFERENCE = 'cam4/user/CHANGE_GENDER_PREFERENCE';
const CHANGE_GENDER_PREFERENCE_ERROR = 'cam4/user/CHANGE_GENDER_PREFERENCE_ERROR';
const CHANGE_GENDER_PREFERENCE_SUCCESS = 'cam4/user/CHANGE_GENDER_PREFERENCE_SUCCESS';
const CHANGE_STUDIO_EMAIL_SETTINGS = 'cam4/user/CHANGE_STUDIO_EMAIL_SETTINGS';
const CONSENT_STATUS = 'cam4/user/CONSENT_STATUS';
const LOGIN = 'cam4/user/LOGIN';
const LOGOUT = 'cam4/user/LOGOUT';
const RECOVER = 'cam4/user/RECOVER';
const REGISTER_STUDIO = 'cam4/studio/REGISTER_STUDIO';
const TOGGLE_PASSWORD_RECOVERY_MODAL = 'cam4/user/PASSWORD_RECOVERY_MODAL';
const UPDATE = 'cam4/user/UPDATE';
const UPDATE_ACTION_STATE = 'cam4/user/UPDATE_ACTION_STATE';
const UPDATE_CAPTCHA = 'cam4/user/UPDATE_CAPTCHA';
const UPDATE_ERROR_MESSAGE_LOGIN = 'cam4/user/UPDATE_ERROR_MESSAGE_LOGIN';
const UPDATE_LOGIN_PASSWORD = 'cam4/user/LOGIN_PASSWORD';
const UPDATE_LOGIN_USERNAME = 'cam4/user/LOGIN_USERNAME';
const UPDATE_PASSWORD_RECOVERY_EMAIL = 'cam4/user/PASSWORD_RECOVERY_EMAIL';
const UPDATE_PASSWORD_RESET_VALIDATION = 'cam4/user/UPDATE_PASSWORD_RESET_VALIDATION';
const UPDATE_SECURITY_ANSWER = 'cam4/user/SECURITY_QUESTION_ANSWER';
const UPDATE_SECURITY_QUESTION = 'cam4/user/SECURITY_QUESTION_UPDATE';
const UPDATE_SETTINGS_CHANGE_PASSWORD_HAS_SUCCEDED = 'cam4/user/UPDATE_SETTINGS_CHANGE_PASSWORD_HAS_SUCCEDED';
const UPDATE_SETTINGS_CURRENT_PASSWORD = 'cam4/user/UPDATE_SETTINGS_CURRENT_PASSWORD';
const UPDATE_SETTINGS_NEW_PASSWORD = 'cam4/user/UPDATE_SETTINGS_NEW_PASSWORD';
const UPDATE_SETTINGS_NEW_PASSWORD_CONFIRM = 'cam4/user/UPDATE_SETTINGS_NEW_PASSWORD_CONFIRM';
const UPDATE_USER_EMAIL = 'cam4/user/UPDATE_USER_EMAIL';
const UPDATE_USER_MODALS = 'cam4/user/UPDATE_USER_MODALS';
const UPDATE_USER_TO_GOLD = 'cam4/user/UPDATE_USER_TO_GOLD';
const USER_STATUS = 'cam4/user/STATUS';

export const NAME = 'user';

const validateCurrentPassword = (password) => password.length;
const validateEmail = (email) => !isEmpty(email) && isEmail(email);
const validatePassword = (password) => validateStrongerPassword(password);
const validateSecondPassword = (passwordNew, passwordConfirm) => passwordNew === passwordConfirm;
const validateSecurityAnswer = (answer) => !isEmpty(answer) && isAlphanumeric(answer);
const validateUsername = (username) => !isEmpty(username);

const initialState = Immutable.fromJS({
  user: {
    username: null,
    accessHash: null,
    isPerformer: false,
    accessLevel: GUEST,
    accountType: null,
    approved2257: null,
    broadcastingConsent: false,
    referrerId: null,
    controlledFeatures: [],
    email: null,
    firebaseShard: null,
    firebaseToken: null,
    studio: {
      studioName: null,
      username: null,
      status: null,
      creationDate: null,
    },
    userGroups: [],
    userModals: [],
    genderPreference: 'female',
    profileImageUrl: null,
    realCountry: 'unknown',
    token: null,
    hasMembershipBonus: null,
    twoFaUserData: null,
    twoFaDismissed: false,
    twoFaEnforced: false,
  },
  state: USER_STATE.USER_ACTION_STATE.NOT_LOADED,
  login: {
    username: {
      value: '',
      isValid: true,
    },
    password: {
      value: '',
      isValid: true,
      errorMessage: '',
    },
    recovery: {
      email: {
        show: false,
        value: '',
        isValid: false,
        code: ENTER_EMAIL,
      },
    },
    security: {
      question: '',
      questionId: '',
      isRequired: false,
      answer: '',
      isValidAnswer: true,
      errorMessage: '',
    },
    captcha: null,
  },
  settings: {
    changePassword: {
      password: {
        value: '',
        isValid: true,
      },
      newPassword: {
        value: '',
        isValid: true,
      },
      newPasswordConfirm: {
        value: '',
        isValid: true,
      },
      state: {
        hasSucceded: true,
        code: '',
      },
    },
    genderPreference: {
      isChanging: false,
      error: '',
    },
  },
  studio: {
    state: null,
    error: null,
  },
  userStatus: null,
  fanClubNotifications: null,
  intercomEnabled: null,
});

// eslint-disable-next-line complexity
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case LOGIN: {
      return state;
    }

    case LOGOUT:
      return state
        .set('state', USER_STATE.USER_ACTION_STATE.NOT_LOADED)
        .set('user', Immutable.fromJS({ ...action.user }));

    case UPDATE:
      return state.set(
        'user',
        Immutable.fromJS({ ...action.user })
      );

    case UPDATE_ACTION_STATE: {
      return state
        .set('state', action.state)
        .setIn(['login', 'security', 'question'], action.question)
        .setIn(['login', 'security', 'questionId'], action.questionId)
        .setIn(['user', 'token'], action.token)
        .set('countryCode', action.countryCode)
        .set('phoneNumber', action.phoneNumber)
        .set('operationId', action.operationId)
        .set('weakPasswordEmail', action.weakPasswordEmail);
    }

    case AUTHENTICATE_USER: {
      if (action.user.username === null || action.user.studio === null) {
        return state.set('state', USER_STATE.USER_ACTION_STATE.LOGIN).set(
          'user',
          Immutable.fromJS({
            ...action.user,
            token: null,
            studio: {
              studioName: null,
              username: null,
              status: null,
              creationDate: null,
            },
          })
        );
      }

      return state
        .set('state', USER_STATE.USER_ACTION_STATE.LOGGED_IN)
        .set('user', Immutable.fromJS({ ...action.user }));
    }

    case UPDATE_LOGIN_USERNAME:
      return state
        .setIn(['login', 'username', 'value'], action.username)
        .setIn(['login', 'username', 'isValid'], validateUsername(action.username));

    case UPDATE_LOGIN_PASSWORD:
      return state
        .setIn(['login', 'password', 'value'], action.password)
        .setIn(['login', 'password', 'isValid'], validatePassword(action.password));

    case UPDATE_CAPTCHA:
      return state.setIn(['login', 'captcha'], action.token);

    case UPDATE_PASSWORD_RECOVERY_EMAIL:
      return state
        .setIn(['recovery', 'email', 'value'], action.email)
        .setIn(['recovery', 'email', 'isValid'], validateEmail(action.email));

    case TOGGLE_PASSWORD_RECOVERY_MODAL:
      return state
        .setIn(['login', 'recovery', 'email', 'show'], action.show)
        .setIn(['login', 'recovery', 'email', 'code'], ENTER_EMAIL);

    case UPDATE_SECURITY_ANSWER:
      return state
        .setIn(['login', 'security', 'answer'], action.answer)
        .setIn(['login', 'security', 'isValidAnswer'], validateSecurityAnswer(action.answer));

    case REGISTER_STUDIO:
      return state.setIn(['studio', 'state'], action.state).setIn(['studio', 'error'], action.errorMessage);

    case UPDATE_SECURITY_QUESTION:
      return state
        .setIn(['login', 'security', 'question'], action.question)
        .setIn(['login', 'security', 'questionId'], action.questionId)
        .setIn(['login', 'security', 'errorMessage'], action.errorMessage)
        .setIn(['login', 'security', 'isValidAnswer'], false)
        .setIn(['login', 'security', 'answer'], '');

    case UPDATE_ERROR_MESSAGE_LOGIN:
      return state
        .setIn(['login', 'password', 'errorMessage'], action.errorMessage)
        .setIn(['login', 'password', 'isValid'], false);

    case UPDATE_SETTINGS_CURRENT_PASSWORD:
      return state
        .setIn(['settings', 'changePassword', 'password', 'value'], action.password)
        .setIn(['settings', 'changePassword', 'password', 'isValid'], validateCurrentPassword(action.password));

    case UPDATE_SETTINGS_NEW_PASSWORD:
      return state
        .setIn(['settings', 'changePassword', 'newPassword', 'value'], action.password.passwordNew)
        .setIn(['settings', 'changePassword', 'newPassword', 'isValid'], validatePassword(action.password.passwordNew))
        .setIn(
          ['settings', 'changePassword', 'newPasswordConfirm', 'isValid'],
          validateSecondPassword(action.password.passwordNew, action.password.passwordConfirm)
        );

    case UPDATE_SETTINGS_NEW_PASSWORD_CONFIRM:
      return state
        .setIn(['settings', 'changePassword', 'newPasswordConfirm', 'value'], action.password.passwordConfirm)
        .setIn(
          ['settings', 'changePassword', 'newPasswordConfirm', 'isValid'],
          validateSecondPassword(action.password.passwordNew, action.password.passwordConfirm)
        );

    case UPDATE_SETTINGS_CHANGE_PASSWORD_HAS_SUCCEDED:
      return state
        .setIn(['settings', 'changePassword', 'state', 'hasSucceded'], action.response)
        .setIn(['settings', 'changePassword', 'state', 'code'], action.code);

    case UPDATE_PASSWORD_RESET_VALIDATION:
      return state.setIn(['settings', 'changePassword'], initialState.getIn(['settings', 'changePassword']));

    case CHANGE_GENDER_PREFERENCE:
      return state.setIn(['settings', 'genderPreference', 'isChanging'], action.isChanging);

    case CHANGE_GENDER_PREFERENCE_ERROR:
      return state
        .setIn(['settings', 'genderPreference', 'isChanging'], action.isChanging)
        .setIn(['settings', 'genderPreference', 'error'], action.error);

    case CHANGE_GENDER_PREFERENCE_SUCCESS:
      return state
        .setIn(['settings', 'genderPreference', 'isChanging'], action.isChanging)
        .setIn(['settings', 'genderPreference', 'error'], action.error)
        .setIn(['user', 'genderPreference'], action.gender);

    case CHANGE_STUDIO_EMAIL_SETTINGS:
      return state
        .setIn(
          ['user', 'studio', 'studioSettings', 'emailPerformerBanned'],
          action.studioEmailSettings.emailPerformerBanned
        )
        .setIn(
          ['user', 'studio', 'studioSettings', 'emailPerformerInviteDecision'],
          action.studioEmailSettings.emailPerformerInviteDecision
        )
        .setIn(
          ['user', 'studio', 'studioSettings', 'emailPerformerVerified'],
          action.studioEmailSettings.emailPerformerVerified
        )
        .setIn(
          ['user', 'studio', 'studioSettings', 'emailStudioAdminVerified'],
          action.studioEmailSettings.emailStudioAdminVerified
        );

    case USER_STATUS:
      return state.setIn(['userStatus'], action.status);

    case RECOVER:
      return state.setIn(['login', 'recovery', 'email', 'code'], action.code);

    case UPDATE_USER_EMAIL:
      return state.setIn(['user', 'email'], action.email);

    case UPDATE_USER_TO_GOLD:
      return state.setIn(['user', 'accessLevel'], GOLD).setIn(['user', 'isAnyTypeOfGold'], true);

    case CONSENT_STATUS:
      return state.setIn(['user', 'broadcastingConsent'], action.status);

    case UPDATE_USER_MODALS:
      return state.setIn(
        ['user', 'userModals'],
        state.getIn(['user', 'userModals']).push(Immutable.fromJS(action.userModals))
      );

    default:
      return state;
  }
}

const user = (state) => state[NAME];
const loginUsername = (state) => state[NAME].getIn(['login', 'username', 'value']);
const loginPassword = (state) => state[NAME].getIn(['login', 'password', 'value']);
const captcha = (state) => state[NAME].getIn(['login', 'captcha']);
const passwordRecoveryEmail = (state) => state[NAME].getIn(['recovery', 'email', 'value']);
const loginUsernameIsValid = (state) => state[NAME].getIn(['login', 'username', 'isValid']);
const loginPasswordIsValid = (state) => state[NAME].getIn(['login', 'password', 'isValid']);
const loginErrorMessage = (state) => state[NAME].getIn(['login', 'password', 'errorMessage']);
const passwordRecoveryEmailIsValid = (state) => state[NAME].getIn(['recovery', 'email', 'isValid']);
const passwordRecoveryModalShow = (state) => state[NAME].getIn(['login', 'recovery', 'email', 'show']);
const passwordRecoveryCode = (state) => state[NAME].getIn(['login', 'recovery', 'email', 'code']);
const securityQuestion = (state) => state[NAME].getIn(['login', 'security', 'question']);
const actionState = (state) => state[NAME].get('state');
const countryCode = (state) => state[NAME].get('countryCode');
const phoneNumber = (state) => state[NAME].get('phoneNumber');
const operationId = (state) => state[NAME].get('operationId');
const weakPasswordEmail = (state) => state[NAME].get('weakPasswordEmail');
const settingsPassword = (state) => state[NAME].getIn(['settings', 'changePassword', 'password', 'value']);
const settingsPasswordIsValid = (state) => state[NAME].getIn(['settings', 'changePassword', 'password', 'isValid']);
const settingsNewPassword = (state) => state[NAME].getIn(['settings', 'changePassword', 'newPassword', 'value']);
const settingsNewPasswordIsValid = (state) =>
  state[NAME].getIn(['settings', 'changePassword', 'newPassword', 'isValid']);
const settingsNewPasswordConfirm = (state) =>
  state[NAME].getIn(['settings', 'changePassword', 'newPasswordConfirm', 'value']);
const settingsNewPasswordConfirmIsValid = (state) =>
  state[NAME].getIn(['settings', 'changePassword', 'newPasswordConfirm', 'isValid']);
const changePasswordHasSucceded = (state) => state[NAME].getIn(['settings', 'changePassword', 'state', 'hasSucceded']);
const changePasswordResponse = (state) => state[NAME].getIn(['settings', 'changePassword', 'state', 'code']);
const studioRegistrationState = (state) => state[NAME].getIn(['studio', 'state']);
const studioRegistrationError = (state) => state[NAME].getIn(['studio', 'error']);
const isGendePreferenceChanging = (state) => state[NAME].getIn(['settings', 'genderPreference', 'isChanging']);
const genderPreferenceError = (state) => state[NAME].getIn(['settings', 'genderPreference', 'error']);
const genderPreference = (state) => state[NAME].getIn(['user', 'genderPreference']);
const accessLevel = (state) => state[NAME].getIn(['user', 'accessLevel']);
const username = (state) => state[NAME].getIn(['user', 'username']);
const email = (state) => state[NAME].getIn(['user', 'email']);
const accessHash = (state) => state[NAME].getIn(['user', 'accessHash']);
const isPerformer = (state) => state[NAME].getIn(['user', 'isPerformer']);
const accountType = (state) => state[NAME].getIn(['user', 'accountType']);
const isAnyTypeOfGold = (state) => isAnyTypeOfGoldHelper(state[NAME].getIn(['user', 'accessLevel']));
const profileImageUrl = (state) => state[NAME].getIn(['user', 'profileImageUrl']);
const realCountry = (state) => state[NAME].getIn(['user', 'realCountry']);
const approved2257 = (state) => state[NAME].getIn(['user', 'approved2257']);
const referrerId = (state) => state[NAME].getIn(['user', 'referrerId']);
const userStatus = (state) => state[NAME].getIn(['userStatus']);
const userModals = (state) => state[NAME].getIn(['user', 'userModals']);
const firebaseShard = (state) => state[NAME].getIn(['user', 'firebaseShard']);
const firebaseToken = (state) => state[NAME].getIn(['user', 'firebaseToken']);
const fanClubNotifications = (state) => state[NAME].getIn(['user', 'fanClubNotifications']);
const intercomEnabled = (state) => state[NAME].getIn(['user', 'intercomEnabled']);
const hasMembershipBonus = (state) => state[NAME].getIn(['user', 'paymentInfo', 'hasMembershipBonus']);
const broadcastingConsent = (state) => state[NAME].getIn(['user', 'broadcastingConsent']);
const twoFaUserData = (state) => state[NAME].getIn(['user', 'twoFaUserData']);
const twoFaDismissed = (state) => state[NAME].getIn(['user', 'twoFaDismissed']);
const twoFaEnforced = (state) => state[NAME].getIn(['user', 'twoFaEnforced']);

function authenticateUser(user) {
  trackingService.emit('authenticate', user);
  MessagingService.emit(GET_USER_DATA_RESPONSE, user);

  return {
    type: AUTHENTICATE_USER,
    user,
  };
}

function logout(payload) {
  return {
    type: LOGOUT,
    user: payload,
  };
}

export function LOGIN_FLOW_V2_REDUX_LOGIN_ACTION(dispatch, data) {
  dispatch(authenticateUser(data));
}

export function LOGIN_FLOW_V2_REDUX_LOGOUT_ACTION(dispatch, data) {
  dispatch(logout(data));
}

function recover(code) {
  return {
    type: RECOVER,
    code,
  };
}

function updateLoginUsername(username) {
  return {
    type: UPDATE_LOGIN_USERNAME,
    username,
  };
}

function updateLoginPassword(password) {
  return {
    type: UPDATE_LOGIN_PASSWORD,
    password,
  };
}

function updateSettingsPassword(password) {
  return {
    type: UPDATE_SETTINGS_CURRENT_PASSWORD,
    password: password.trim(),
  };
}

function updateSettingsNewPassword(passwordNew, passwordConfirm) {
  return {
    type: UPDATE_SETTINGS_NEW_PASSWORD,
    password: {
      passwordNew: passwordNew.trim(),
      passwordConfirm: passwordConfirm.trim(),
    },
  };
}

function updateChangePasswordHasSucceded(response, code = '') {
  return {
    type: UPDATE_SETTINGS_CHANGE_PASSWORD_HAS_SUCCEDED,
    code,
    response,
  };
}

function updateSettingsNewPasswordConfirm(passwordConfirm, passwordNew) {
  return {
    type: UPDATE_SETTINGS_NEW_PASSWORD_CONFIRM,
    password: {
      passwordNew,
      passwordConfirm,
    },
  };
}

function updatePasswordRecovery(email) {
  return {
    type: UPDATE_PASSWORD_RECOVERY_EMAIL,
    email,
  };
}

function updateCaptcha(token) {
  return {
    type: UPDATE_CAPTCHA,
    token,
  };
}

function togglePasswordRecoveryModal(val) {
  return {
    type: TOGGLE_PASSWORD_RECOVERY_MODAL,
    show: val,
  };
}

function updateSecurityAnswer(answer) {
  return {
    type: UPDATE_SECURITY_ANSWER,
    answer,
  };
}

function updateSecurityQuestionsVals(question, questionId, errorMessage) {
  return {
    type: UPDATE_SECURITY_QUESTION,
    question,
    questionId,
    errorMessage,
  };
}

function updateErrorMessageLogin(errorMessage) {
  return {
    type: UPDATE_ERROR_MESSAGE_LOGIN,
    errorMessage,
  };
}

function updateActionState(
  state,
  question,
  questionId,
  token,
  countryCode,
  phoneNumber,
  operationId,
  weakPasswordEmail
) {
  return {
    type: UPDATE_ACTION_STATE,
    state,
    question,
    questionId,
    token,
    countryCode,
    phoneNumber,
    operationId,
    weakPasswordEmail,
  };
}

function dismissPasswordChanged() {
  return { type: UPDATE_PASSWORD_RESET_VALIDATION };
}

function registerStudio({ state, errorMessage }) {
  return {
    type: REGISTER_STUDIO,
    state,
    errorMessage,
  };
}

function changeStudioEmailSettings(studioEmailSettings) {
  return {
    type: CHANGE_STUDIO_EMAIL_SETTINGS,
    studioEmailSettings,
  };
}

function setUserStatus(status) {
  return {
    type: USER_STATUS,
    status,
  };
}

function setBroadcastingConsentStatus(status) {
  return {
    type: CONSENT_STATUS,
    status,
  };
}

function updateUserModals(userModals) {
  return {
    type: UPDATE_USER_MODALS,
    userModals,
  };
}

function updateUserEmail(email) {
  return {
    type: UPDATE_USER_EMAIL,
    email,
  };
}

function doLogin(username, password, captcha, useV3) {
  const query = {
    username,
    password,
  };

  if (captcha) {
    if (useV3) {
      query.gcaptchaV3 = captcha;
    } else {
      query.gcaptcha = captcha;
    }
  }

  return (dispatch) =>
    api
      .post(`${LOGIN_URL}`, qs.stringify(query), {
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          ...TracingService.generateHeaders(),
        },
      })
      .then((response) => {
        if (response.code === PASS_REC_EMAIL_SENT) {
          throw response;
        } else {
          return dispatch(authenticateUser(response.data));
        }
      })
      .catch((res) => {
        if (res.status) {
          if (res.status === WEAK_PASSWORD) {
            dispatch(updateActionState(USER_STATE.getState(res.status), '', '', '', '', '', '', res.email));
          }
        } else {
          switch (res.code) {
            case 'consecutive_login_failed':
              dispatch(updateErrorMessageLogin('consecutive_login_failed'));
              break;
            case PASS_REC_EMAIL_SENT:
              throw res;
            default: {
              if (res.data) {
                dispatch(
                  updateActionState(
                    USER_STATE.getState(res.code),
                    res.data.question || '',
                    res.data.questionId || '',
                    res.data.token || '',
                    res.data.countryCode || '',
                    res.data.phoneNumber || '',
                    res.data.operationId || '',
                    res.data.weakPasswordEmail || ''
                  )
                );
              } else {
                dispatch(updateActionState(USER_STATE.getState(res.code), '', '', ''));
              }
            }
          }
        }
      });
}

function doLogout() {
  sessionStorage.removeItem(IMPERSONATE_KEY);

  return (dispatch) =>
    api.post(LOGOUT_URL).then((res) => {
      dispatch(logout(res.data));
      dispatch(dispatch(updateActionState(USER_STATE.USER_ACTION_STATE.LOGIN, '', '', '')));
      MessagingService.emit(GET_USER_DATA_RESPONSE, res.data);
      MessagingService.emit(USER_LOGGED_OUT, res.data);
    });
}

function doRegistration(username, password, email, goldSignup, promotions) {
  return () =>
    api.post(
      REGISTRATION_URL,
      qs.stringify({
        userName: username,
        newPassword: password,
        email,
        promotions,
        goldSignup,
      })
    );
}

function doPasswordRecovery(email, isValid) {
  return (dispatch) => {
    const config = {
      data: qs.stringify({ email }),
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        ...TracingService.generateHeaders(),
      },
    };

    if (isValid) {
      ApiService
        .post(PASSWORD_RECOVERY_URL, config)
        .then(() => {
          dispatch(recover(EMAIL_CONFIRMED));
        })
        .catch((err) => {
          const error = JSON.parse(err.request.response);
          switch (error.code) {
            case CODES.UNVERIFIED_EMAIL_CODE:
              dispatch(recover(UNVERIFIED_EMAIL));
              break;
            case CODES.SECONDARY_EMAIL_CODE:
              dispatch(recover(INVALID_SECONDARY_EMAIL));
              break;
            default:
              dispatch(recover(INVALID_EMAIL));
          }
        });
    }
  };
}

function resetUserLoginState() {
  return (dispatch) => {
    dispatch(updateActionState(USER_STATE.USER_ACTION_STATE.LOGIN, '', '', ''));
    dispatch(updateErrorMessageLogin(''));
  };
}

function doStudioRegistration(fields, useV3) {
  const studioRegObj = {
    studioName: fields.studioName.value,
    studioOwnerName: fields.username.value,
    email: fields.email.value,
    password: fields.password.value,
  };

  if (fields.captcha) {
    if (useV3) {
      studioRegObj.captchaV3 = fields.captcha;
    } else {
      studioRegObj.captcha = fields.captcha;
    }
  }

  return (dispatch) => {
    const config = {
      data: qs.stringify(studioRegObj),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    };

    ApiService
      .post(STUDIO_REGISTRATION_URL, config)
      .then((res) => {
        dispatch(
          registerStudio({
            state: USER_STATE.USER_ACTION_STATE.STUDIO_REGISTERED,
            errorMessage: false,
          })
        );

        return res;
      });
  };
}

function resetStudioRegistrationState() {
  return (dispatch) =>
    dispatch(
      registerStudio({
        state: null,
        errorMessage: null,
      })
    );
}

function dismissStudioRegistrationMessage() {
  return (dispatch) => {
    dispatch(
      registerStudio({
        state: null,
        errorMessage: null,
      })
    );
  };
}

function dismissChangePasswordMessage() {
  return (dispatch) => {
    dispatch(updateChangePasswordHasSucceded(true, ''));
  };
}

export function getUserState(showUpgradeToRegular, showGuestBillingModal) {
  return (dispatch) =>
    api.get(USER_STATE_URL).then((res) => {
      if (showUpgradeToRegular) {
        showGuestBillingModal(true);
      }

      return dispatch(authenticateUser(res));
    });
}

function updateUserStatusToGold() {
  return {
    type: UPDATE_USER_TO_GOLD,
    isChanging: true,
  };
}

function changePassword(currentPassword, newPassword, newPasswordConfirm, username) {
  return (dispatch) => {
    dispatch(updateChangePasswordHasSucceded(true, 'PENDING'));

    return api
      .post(
        CHANGE_PASSSWORD_URL + username,
        qs.stringify({
          currentPassword,
          newPassword,
          newPasswordConfirm,
          action: 'save',
        })
      )
      .then((res) => {
        if (dispatch(dismissPasswordChanged())) {
          dispatch(updateChangePasswordHasSucceded(true, res.code));
        }

        return Promise.resolve(res);
      })
      .catch((error) => {
        dispatch(updateChangePasswordHasSucceded(false, error.code));

        return Promise.reject(error);
      });
  };
}

function updateSecurityQuestions(username, errorMessage) {
  return (dispatch) => {
    const url = ApiService.substitute(LOGIN_SECURITY_QUESTIONS_URL, { username });

    ApiService
      .get(url)
      .then((res) =>
        dispatch(
          updateSecurityQuestionsVals(res.data.data.question || '', res.data.data.questionId || '', errorMessage)
        )
      );
  };
}

function initChangeGenderPreference() {
  return {
    type: CHANGE_GENDER_PREFERENCE,
    isChanging: true,
  };
}

function changeGenderPreferenceSuccess(gender) {
  MessagingService.emit(SET_USER_GENDER_PREFERENCE, gender);

  return {
    type: CHANGE_GENDER_PREFERENCE_SUCCESS,
    isChanging: false,
    error: '',
    gender,
  };
}

function changeGenderPreferenceError() {
  return {
    type: CHANGE_GENDER_PREFERENCE_ERROR,
    isChanging: false,
    error: 'There was an error with API call',
  };
}

function updateGenderPreference(username, gender) {
  const URL = GENDER_PREFERENCE_URL.replace('{username}', username);

  return (dispatch) => {
    dispatch(initChangeGenderPreference());

    return api
      .post(URL, qs.stringify({ gender }), { headers: { 'content-type': 'application/x-www-form-urlencoded' } })
      .then(() => dispatch(changeGenderPreferenceSuccess(gender)))
      .catch(() => dispatch(changeGenderPreferenceError()));
  };
}

function verifySecurityAnswer(username, password, json) {
  const headers = { 'content-type': 'application/x-www-form-urlencoded' };

  return (dispatch) =>
    api
      .post(LOGIN_VERIFY_SECURITY_QUESTIONS_URL, qs.stringify(json), { params: json }, headers)
      .then(() => dispatch(doLogin(username, password)))
      .catch((res) => {
        switch (res.code) {
          case 'client_temp_banned':
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.account.temporarily.banned'));
            break;
          case 'client_banned':
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.account.banned'));
            break;
          case 'client_unknown':
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.account.unknown'));
            break;
          case 'invalid_answer':
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.answer.invalid'));
            break;
          case 'could_not_login':
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.account.problems.login'));
            break;
          case 'client_uuid_failed':
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.account.problems.login'));
            break;
          case 'consecutive_login_failed':
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.account.multiple.login'));
            break;
          default:
            dispatch(updateSecurityQuestions(username, 'homepage.v2.studio.account.server.down'));
        }
      });
}

function postStudioEmailSettings(studioOwnerName, settings) {
  return (dispatch) => {
    api
      .post(STUDIO_EMAIL_SETTINGS.replace('{studioOwnerName}', studioOwnerName), qs.stringify(settings))
      .catch((error) => logger.error(error));

    return dispatch(changeStudioEmailSettings(settings));
  };
}

function getUserStatus(username) {
  const url = ApiService.substitute(USER_STATUS_URL, { username: encodeURIComponent(usernameOrImpersonate(username)) });

  return (dispatch) => {
    ApiService
      .get(url)
      .then((res) => {
        dispatch(setUserStatus(res.data.status));
      })
      .catch((error) => {
        dispatch(setUserStatus('NOT_FOUND'));
        logger.error(error);
      });
  };
}

function getConsentStatus(username) {
  if (username) {
    const url = ApiService.substitute(
      USER_CONSENT_URL_STATUS_APPROVED,
      { username: encodeURIComponent(username) }
    );

    return (dispatch) => {
      MessagingService.emit(GET_USER_CONSENT_STATUS);
      ApiService
        .get(url)
        .then((res) => {
          if (res?.data?.userConsentStatus === 'APPROVED') {
            dispatch(setBroadcastingConsentStatus(true));
          }

          MessagingService.emit(GET_USER_CONSENT_STATUS_RESPONSE, res.data);
        })
        .catch((error) => {
          MessagingService.emit(GET_USER_CONSENT_STATUS_ERROR);
          parseCatch(error);
        });
    };
  }

  return undefined;
}

function validateRegistrationUsername(username) {
  const url = ApiService.substitute(VALIDATE_USERNAME_REGISTRATION_URL, { username: encodeURIComponent(username) });

  return () =>
    ApiService
      .get(url)
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
}

function validateRegistrationEmail(email) {
  const url = ApiService.substitute(VALIDATE_EMAIL_REGISTRATION_URL, { email: encodeURIComponent(email) });

  return () =>
    ApiService
      .get(url)
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
}

function newUserRegistration(username, password, email, signUpType, promotions, captcha, useV3, genderPreferenceProps) {
  const utmTracking = JSON.parse(localStorage.getItem(TRACKING.UTM_TRACKING));

  const registrationObj = {
    username,
    password,
    email,
    allowPromotionsConsent: promotions,
    signUpType,
    genderPreference: genderPreferenceProps,
  };

  if (utmTracking && Object.keys(utmTracking).length) {
    registrationObj.utmTracking = utmTracking;
  }

  if (captcha !== null) {
    if (useV3) {
      registrationObj.captchaV3 = captcha;
    } else {
      registrationObj.captcha = captcha;
    }
  }

  const config = {
    data: registrationObj,
    headers: { 'X-Redirect-To': window.location.href },
  };

  return () =>
    ApiService
      .post(USER_CREATE_URL, config)
      .then((res) => {
        localStorage.removeItem(TRACKING.UTM_TRACKING);

        return Promise.resolve(res.data);
      })
      .catch((err) => Promise.reject(err.response.data));
}

export const selector = createStructuredSelector({
  user: createStructuredSelector({
    user,
    loginUsername,
    loginPassword,
    captcha,
    loginUsernameIsValid,
    loginPasswordIsValid,
    loginErrorMessage,
    passwordRecoveryEmail,
    passwordRecoveryEmailIsValid,
    passwordRecoveryModalShow,
    passwordRecoveryCode,
    securityQuestion,
    actionState,
    studioRegistrationError,
    studioRegistrationState,
    genderPreference,
    accessLevel,
    username,
    accessHash,
    accountType,
    isAnyTypeOfGold,
    isPerformer,
    profileImageUrl,
    realCountry,
    approved2257,
    referrerId,
    userStatus,
    userModals,
    firebaseShard,
    firebaseToken,
    email,
    fanClubNotifications,
    hasMembershipBonus,
    broadcastingConsent,
    phoneNumber,
    countryCode,
    operationId,
    weakPasswordEmail,
    intercomEnabled,
    twoFaUserData,
    twoFaDismissed,
    twoFaEnforced,
  }),
  settings: createStructuredSelector({
    changePasswordResponse,
    changePasswordHasSucceded,
    settingsPassword,
    settingsPasswordIsValid,
    settingsNewPassword,
    settingsNewPasswordIsValid,
    settingsNewPasswordConfirm,
    settingsNewPasswordConfirmIsValid,
    isGendePreferenceChanging,
    genderPreferenceError,
  }),
});

export const actionCreators = {
  doLogin,
  doLogout,
  doRegistration,
  doPasswordRecovery,
  updatePasswordRecovery,
  togglePasswordRecoveryModal,
  updateLoginUsername,
  updateLoginPassword,
  updateSecurityAnswer,
  updateCaptcha,
  getUserState,
  updateSecurityQuestions,
  updateGenderPreference,
  verifySecurityAnswer,
  changePassword,
  updateSettingsPassword,
  updateSettingsNewPassword,
  updateSettingsNewPasswordConfirm,
  doStudioRegistration,
  resetStudioRegistrationState,
  dismissStudioRegistrationMessage,
  dismissChangePasswordMessage,
  postStudioEmailSettings,
  getUserStatus,
  resetUserLoginState,
  updateUserEmail,
  getConsentStatus,
  updateUserModals,
  updateUserStatusToGold,
  validateRegistrationUsername,
  validateRegistrationEmail,
  newUserRegistration,
  updateChangePasswordHasSucceded,
  recover,
};
